import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import * as contentful from "contentful";

import { css } from "@emotion/css";
import { desktop, laptop, tablet } from "../../../styles/breakpoints";
import Clickable from "../../../components/Clickable";
import buttons from "../../../styles/buttons";
import { useI18n } from "../../../i18n";

const Posts = () => {
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const { t } = useI18n();

  useEffect(() => {
    const client = contentful.createClient({
      space: process.env.REACT_APP_CONTENTFUL_ID,
      accessToken: process.env.REACT_APP_CONTENTFUL_CDK,
    });

    client
      .getEntries({ content_type: "blog", order: "fields.date" })
      .then(function (entries) {
        let items = entries?.items;

        if (entries) {
          setPosts(items);
        }
      });
  }, []);

  return (
    <div className={style.container}>
      <h2>
        <span className="journal-title">{t("blog_insights")}</span>
        <br />
        <span className="journal-sub">{t("blog_smarter_style")}</span>
      </h2>
      <div className="wrapper">
        {Object.values(posts).map((v, i) => {
          if (i < page * 12) {
            return (
              <Link key={i} className="post" to={`/post/${v.fields.slug}`}>
                <img
                  src={v.fields?.image?.fields?.file?.url}
                  alt={v.fields.title}
                />
                <div className="content">
                  <h5>{v.fields.title}</h5>
                  <div>
                    <span>{v.fields.category}</span>
                    <span>
                      {format(new Date(v.fields.date), "LLLL dd, yyyy")}
                    </span>
                  </div>
                  <p>{v.fields.description}</p>
                </div>
              </Link>
            );
          }
          return <></>;
        })}
      </div>
      <Clickable className={buttons.primary} onClick={() => setPage(page + 1)}>
        {t("blog_load_more")}
      </Clickable>
    </div>
  );
};

const style = {
  container: css({
    paddingTop: 135,
    paddingBottom: 180,
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    alignContent: "center",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: 30,
    paddingRight: 30,
    backgroundColor: "#fff",

    h2: {
      color: "#121212",
      textAlign: "center",
      fontFamily: "Syne",
      fontSize: 48,
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "64px",
      letterSpacing: "-0.48px",
      marginBottom: 72,
      maxWidth: 800,

      ".journal-title": {
        fontSize: 52.8,
        lineHeight: "64px",
      },
      ".journal-sub": {
        fontSize: 40.8,
        lineHeight: "50px",
      },
    },
    ".wrapper": {
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "row",
      alignContent: "center",
      justifyContent: "center",
      alignItems: "stretch",
      gap: 50,
      marginBottom: 80,
      maxWidth: 1190,
      marginLeft: 40,
      marginRight: 40,

      ".post": {
        width: 360,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        borderRadius: 50,
        border: "1px solid rgba(255, 174, 105, 0.20)",
        background: "#FFF8F2",
        overflow: "hidden",

        img: {
          width: "100%",
          height: "auto",
        },

        ".content": {
          padding: 24,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 16,
        },
        h5: {
          color: "#21243D",
          fontFamily: "Syne",
          fontSize: 26,
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "31.5px",
        },
        p: {
          color: "#21243D",
          fontFamily: "Inter",
          fontSize: 14,
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "normal",
        },
        div: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 15,

          span: {
            color: "#FFF",
            textAlign: "center",
            fontFamily: "Syne",
            fontSize: 15,
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "normal",
            borderRadius: 100,
            background: "#D7B7FD",
            padding: "8px 16px",
          },
        },
      },
    },
    ".load-btn": {
      borderRadius: 30,
      border: "2px solid #FF6AFF",
      background: "rgba(168, 239, 18, 0.20)",
      boxShadow: "5px 5px 0px 0px #FF6AFF",
      backdropFilter: "blur(9.399999618530273px)",
      color: "#000",
      fontFamily: "Syne",
      fontSize: 16.971,
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "normal",
      letterSpacing: 0.339,
      textTransform: "uppercase",
      padding:30,
      maxWidth: 200,
      textAlign: 'center',
      width: '100%'
    },
    [desktop]: {
      h2: {
        fontSize: 60,
        lineHeight: "65px",
        letterSpacing: "-0.6px",
        marginBottom: 80,

        ".journal-title": {
          fontSize: 66,
          lineHeight: "65px",
        },
        ".journal-sub": {
          fontSize: 42,
          lineHeight: "60px",
        },
      },
      ".post": {
        width: 327,
        borderRadius: 50,

        h5: {
          fontSize: 26,
          lineHeight: "28px",
        },
        p: {
          fontSize: 16,
        },
        div: {
          gap: 20,

          span: {
            fontSize: 16,
          },
        },
      },

      ".load-btn": {
        fontSize: 16,
        maxWidth: 180,
        borderRadius: 27.236,
        padding:25
      },
    },
    [laptop]: {
      h2: {
        fontSize: 42,
        lineHeight: "47px",
        letterSpacing: "-0.42px",
        marginBottom: 60,

        ".journal-title": {
          fontSize: 46.2,
          lineHeight: "47px",
        },
        ".journal-sub": {
          fontSize: 32,
          lineHeight: "44px",
        },
      },
      ".post": {
        width: "44% !important",
        borderRadius: 50,
        gap: 16,

        h5: {
          fontSize: 24,
          lineHeight: "26px",
        },
        p: {
          fontSize: 14,
        },
        div: {
          gap: 8,

          span: {
            fontSize: 14,
          },
        },
      },

      ".load-btn": {
        fontSize: 14,
        maxWidth: 160,
        borderRadius: 24.801,
        padding:25
      },
    },
    [tablet]: {
      h2: {
        fontSize: 34,
        lineHeight: "47px",
        letterSpacing: "-0.34px",
        marginBottom: 42,

        ".journal-title": {
          fontSize: 37.4,
          lineHeight: "40px",
        },
                ".journal-sub": {
          fontSize: 20,
          lineHeight: "26px",
        },
        "@media (max-width: 480px)": {
          ".journal-sub": {
            fontSize: 18,
            lineHeight: "22px",
          },
        },
      },
      ".wrapper": {
        marginLeft: 0,
        marginRight: 0
      },

      ".post": {
        maxWidth: 300,
        width: "100% !important",
        borderRadius: 36,
        gap: 0,
        paddingBottom: 10,

        h5: {
          fontSize: '18px !important',
          lineHeight: "23px",
        },
        p: {
          fontSize: '14px !important',
        },
        div: {
          gap: 8,

          span: {
            fontSize: '12px !important',
          },
        },
      },

      ".load-btn": {
        fontSize: 12,
        maxWidth: 140,
        padding:22,
        borderRadius: 21.943,

      },
    },
  }),
};

export default Posts;
